import type { Item, StockLevel, Warehouse } from '@erp/shared';
import { Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Combobox, type ComboboxOption, FormField } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { cn } from '../../lib/cn.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

export interface AdjustStockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current stock levels — powers the live on-hand readout + avg-cost prefill. */
  levels: StockLevel[];
  /** Row-action prefill (item + warehouse); null for the page-level button. */
  initial: { itemId: string; warehouseId: string } | null;
  /**
   * Pin the dialog to ONE item (the item's own Stock tab). The picker is replaced
   * by a read-only row and the whole-catalogue fetch is skipped — that fetch pages
   * through /items 500 at a time, which is pure waste when the item can't change.
   */
  lockedItem?: { id: string; name: string; code: string };
  /** Called after a successful adjustment (re-fetch levels, etc.). */
  onDone: () => void | Promise<void>;
}

const selectCls = 'h-10 w-full rounded-md border border-border bg-bg px-3 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20';

/**
 * Manual stock adjustment (POST /stock/adjust): pick an item + warehouse,
 * increase or decrease by a quantity, with an optional unit cost on increases
 * (blends into the moving average — the server ignores cost on decreases, so
 * the field is hidden then). Client-side it refuses to decrease below the
 * available on-hand; numeric inputs are Arabic-digit safe.
 */
export function AdjustStockDialog({ open, onOpenChange, levels, initial, lockedItem, onDone }: AdjustStockDialogProps): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';

  // The full item catalogue, loaded page-by-page once per mount — the stock
  // table only lists items that already have a level row, but an adjustment
  // must be able to target ANY item (first-time stock too).
  const [catalog, setCatalog] = useState<Item[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);

  const [itemId, setItemId] = useState<string | undefined>(undefined);
  const [warehouseId, setWarehouseId] = useState('');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('increase');
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [costCurrency, setCostCurrency] = useState(baseCurrency);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Depend on the pinned item's ID, never the object: a caller that builds
  // `lockedItem` inline would otherwise hand us a new identity every render and
  // re-run these effects — resetting the form out from under whoever is typing.
  const lockedId = lockedItem?.id;

  // Reset the form each time the dialog opens (fresh or row-prefilled).
  useEffect(() => {
    if (!open) return;
    setItemId(lockedId ?? initial?.itemId);
    setWarehouseId(initial?.warehouseId ?? '');
    setDirection('increase');
    setQty('');
    setNote('');
    setError(null);
  }, [open, initial, lockedId]);

  useEffect(() => {
    if (!open || catalog || lockedId) return;
    let on = true;
    void (async () => {
      try {
        const all: Item[] = [];
        for (let offset = 0; offset < 10_000; offset += 500) {
          const r = await api().get<{ items: Item[] }>('/items', { params: { limit: 500, offset } });
          all.push(...r.data.items);
          if (r.data.items.length < 500) break;
        }
        if (on) setCatalog(all.filter((i) => i.isActive));
      } catch {
        if (on) setCatalog([]);
      }
    })();
    return () => { on = false; };
  }, [open, catalog, lockedId]);

  useEffect(() => {
    if (!open || warehouses) return;
    void api()
      .get<{ items: Warehouse[] }>('/warehouses')
      .then((r) => setWarehouses(r.data.items.filter((w) => w.isActive)))
      .catch(() => setWarehouses([]));
  }, [open, warehouses]);

  // Default the warehouse when there's only one.
  useEffect(() => {
    if (open && !warehouseId && warehouses?.length === 1) setWarehouseId(warehouses[0]!.id);
  }, [open, warehouseId, warehouses]);

  const itemOptions: ComboboxOption[] = useMemo(() => {
    const opts = new Map<string, ComboboxOption>();
    // Seed from the visible levels so a row-prefilled item resolves instantly,
    // before the full catalogue finishes loading.
    for (const l of levels) {
      if (!opts.has(l.itemId)) opts.set(l.itemId, { value: l.itemId, label: l.itemName, sublabel: l.itemCode, keywords: l.itemCode });
    }
    for (const it of catalog ?? []) {
      opts.set(it.id, { value: it.id, label: it.name, sublabel: it.code, keywords: `${it.code} ${it.barcode ?? ''}` });
    }
    return [...opts.values()];
  }, [levels, catalog]);

  // On-hand for the chosen item, fetched per item rather than read out of the
  // `levels` prop. The stock page is server-paged, so that prop holds one page —
  // picking an item from the catalogue that happens to be on page 4 would
  // otherwise read as "0 on hand" and block a decrease the server would allow.
  const [itemLevels, setItemLevels] = useState<StockLevel[] | null>(null);
  useEffect(() => {
    if (!open || !itemId) { setItemLevels(null); return; }
    let on = true;
    void api()
      .get<{ items: StockLevel[] }>('/stock/levels', { params: { itemId } })
      .then((r) => { if (on) setItemLevels(r.data.items); })
      .catch(() => { if (on) setItemLevels([]); });
    return () => { on = false; };
  }, [open, itemId]);

  // Live on-hand for the chosen item + warehouse (no row ⇒ 0, same as the server).
  const level = useMemo(() => {
    if (!itemId || !warehouseId) return null;
    const match = (l: StockLevel): boolean => l.itemId === itemId && l.warehouseId === warehouseId;
    return (itemLevels ?? []).find(match) ?? levels.find(match) ?? null;
  }, [itemLevels, levels, itemId, warehouseId]);
  // The currency the level's AVERAGE lives in (null until it has a cost basis).
  const levelCurrency = level?.avgCostCurrencyCode ?? null;
  // A cost typed in another currency converts into the average at the day's
  // rate — only when there IS an average to protect (something on hand).
  const willConvert =
    direction === 'increase' && levelCurrency !== null &&
    Number(level?.quantity ?? '0') > 0 && costCurrency !== levelCurrency;

  // Prefill the unit cost with the current average when targeting picks change.
  useEffect(() => {
    setCost(level && Number(level.avgCost) > 0 ? trimMoney(level.avgCost) : '');
    setCostCurrency(level?.avgCostCurrencyCode ?? baseCurrency);
    // `itemLevels` is in the list so the prefill lands once the per-item fetch
    // resolves; `level` itself is derived from it and would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, warehouseId, open, itemLevels]);

  const qtyNorm = normalizeNumericInput(qty).trim();
  const qtyD = safeDecimal(qtyNorm).toDecimalPlaces(4);
  const qtyValid = qtyNorm !== '' && qtyD.gt(0);
  const availD = safeDecimal(level?.quantity ?? '0');
  // Mirror of the business rule: you cannot take out more than is on hand.
  const decreaseTooMuch = direction === 'decrease' && qtyValid && qtyD.gt(availD);
  const afterD = direction === 'decrease' ? availD.minus(qtyD) : availD.plus(qtyD);
  const canSubmit = Boolean(itemId && warehouseId && qtyValid && !decreaseTooMuch);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    // The dialog is portalled, but React still bubbles events up the COMPONENT
    // tree — so when this is opened from inside another <form> (the item's Stock
    // tab) the host form's onSubmit fired too, saving the item and navigating
    // away mid-adjustment. This submit belongs to the dialog alone.
    e.stopPropagation();
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    const body: Record<string, unknown> = {
      itemId,
      warehouseId,
      quantity: (direction === 'decrease' ? qtyD.neg() : qtyD).toString(),
    };
    // Cost only matters on increases (the server dispatches decreases at the
    // current average); optional — omitted, the average stays untouched.
    if (direction === 'increase') {
      const costNorm = normalizeNumericInput(cost).trim();
      if (costNorm !== '') {
        const c = safeDecimal(costNorm).toDecimalPlaces(4);
        if (c.gte(0)) {
          body.unitCost = c.toString();
          body.unitCostCurrencyCode = costCurrency;
        }
      }
    }
    if (note.trim()) body.note = note.trim();
    try {
      await api().post('/stock/adjust', body);
      toast.success(t('stock.adjust.done'));
      await onDone();
      onOpenChange(false);
    } catch (err) {
      const data = (err as {
        response?: { data?: { code?: string; cause?: { code?: string; incoming?: string; existing?: string } } };
      }).response?.data;
      const code = data?.cause?.code ?? data?.code;
      if (code === 'adjust_cost_no_rate') {
        setError(t('stock.adjust.errors.costNoRate', {
          from: data?.cause?.incoming ?? costCurrency,
          to: data?.cause?.existing ?? levelCurrency ?? baseCurrency,
        }));
      }
      else if (code === 'adjust_cost_currency_mismatch') setError(t('stock.adjust.errors.costCurrencyMismatch'));
      else if (code === 'stock.insufficient') setError(t('stock.adjust.errors.insufficient', { defaultValue: 'Not enough stock on hand for that decrease.' }));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  const qtyError = decreaseTooMuch
    ? t('stock.adjust.errors.decreaseTooMuch', { available: level?.quantity ?? '0' })
    : undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('stock.adjust.title')}</DialogTitle>
          <DialogDescription>{t('stock.adjust.description')}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <FormField id="adj-item" label={t('stock.adjust.item')} required>
            {lockedItem ? (
              <div id="adj-item" className="flex h-10 items-center gap-2 rounded-md border border-border bg-bg-subtle px-3 text-sm">
                <span className="font-mono text-xs text-fg-muted">{lockedItem.code}</span>
                <span className="truncate">{lockedItem.name}</span>
              </div>
            ) : (
              <Combobox
                id="adj-item"
                options={itemOptions}
                value={itemId}
                onChange={(v) => setItemId(v)}
                placeholder={catalog ? t('stock.adjust.itemPlaceholder') : t('stock.adjust.loadingItems')}
                ariaLabel={t('stock.adjust.item')}
              />
            )}
          </FormField>
          <FormField id="adj-warehouse" label={t('stock.adjust.warehouse')} required>
            <select
              id="adj-warehouse"
              className={selectCls}
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              required
            >
              <option value="" disabled>{t('stock.adjust.selectWarehouse')}</option>
              {(warehouses ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.code} · {w.name}</option>
              ))}
            </select>
          </FormField>

          {/* Live on-hand for the chosen item + warehouse. */}
          {itemId && warehouseId && (
            <div className="rounded-md bg-bg-subtle px-3 py-2 text-sm text-fg-muted">
              {t('stock.adjust.onHand')}:{' '}
              <span className="font-mono font-semibold text-fg" dir="ltr">{trimMoney(level?.quantity ?? '0')}</span>
              {level?.avgCostCurrencyCode && (
                <>
                  {' · '}{t('stock.columns.avgCost')}:{' '}
                  <span className="font-mono">
                    {formatMoney(level.avgCost, { locale: i18n.language, currency: level.avgCostCurrencyCode })}
                  </span>
                </>
              )}
              {qtyValid && !decreaseTooMuch && (
                <>
                  {' · '}{t('stock.adjust.after')}:{' '}
                  <span className="font-mono font-semibold text-fg" dir="ltr">{afterD.toString()}</span>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField id="adj-direction" label={t('stock.adjust.direction')}>
              <div className="inline-flex h-10 items-center rounded-full border border-border p-0.5">
                {(['increase', 'decrease'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={direction === d}
                    onClick={() => setDirection(d)}
                    className={cn(
                      'inline-flex h-8 flex-1 items-center justify-center gap-1 rounded-full px-3 text-xs font-semibold transition',
                      direction === d ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg',
                    )}
                  >
                    {d === 'increase' ? <Plus className="h-3.5 w-3.5" aria-hidden /> : <Minus className="h-3.5 w-3.5" aria-hidden />}
                    {t(`stock.adjust.${d}`)}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField id="adj-qty" label={t('stock.adjust.quantity')} required error={qtyError}>
              <Input
                id="adj-qty"
                dir="ltr"
                inputMode="decimal"
                autoComplete="off"
                value={qty}
                onChange={(e) => setQty(normalizeNumericInput(e.target.value))}
                required
              />
            </FormField>
          </div>

          {/* Unit cost only applies to increases — the server dispatches
              decreases at the current average cost regardless. */}
          {direction === 'increase' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_8rem]">
              <FormField
                id="adj-cost"
                label={t('stock.adjust.unitCost', { currency: costCurrency })}
                helperText={willConvert
                  ? t('stock.adjust.convertHint', { from: costCurrency, to: levelCurrency })
                  : t('stock.adjust.unitCostHint')}
              >
                <Input
                  id="adj-cost"
                  dir="ltr"
                  inputMode="decimal"
                  autoComplete="off"
                  value={cost}
                  onChange={(e) => setCost(normalizeNumericInput(e.target.value))}
                />
              </FormField>
              <FormField id="adj-cost-ccy" label={t('stock.adjust.currency')}>
                <CurrencySelect
                  id="adj-cost-ccy"
                  value={costCurrency}
                  onChange={setCostCurrency}
                  className={selectCls}
                  aria-label={t('stock.adjust.currency')}
                />
              </FormField>
            </div>
          )}

          <FormField id="adj-note" label={t('stock.adjust.note')}>
            <Input
              id="adj-note"
              value={note}
              maxLength={2000}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('stock.adjust.notePlaceholder')}
            />
          </FormField>

          {error && <p className="text-sm text-danger" role="alert">{error}</p>}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('stock.adjust.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!canSubmit}>
              {t('stock.adjust.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
